import * as E from 'fp-ts/lib/Either';
import {isEmpty} from 'lodash';

import {addQueryToPath, parseQueryString} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {collectEnumValues} from '../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  computeIsGlobalNamespace,
  globalNamespace,
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {AppRoutesPrefixesEnum} from '../../../libs/shared/ui/src/routing/appRoutesPrefixes';
import {
  AnalyticsReportFilterOptions,
  AnalyticsSavedViewFiltersModel,
  RAnalyticsReportFilterOptionsDirty,
  RAnalyticsSavedViewFiltersDirty,
} from '../../models/api/analyticsSavedViewModel';
import {AnalyticsParameters} from '../../store/panels/analytics/parameters/analyticsParameters';
import {
  AnalyticsSelectedViewId,
  DEFAULT_VIEW_ID,
  isAnalyticsViewTemporary,
  isSelectedViewAllData,
  RAnalyticsSelectedViewIdDirty,
} from '../../store/panels/analytics/views/analyticsPanelViewsState';
import {
  AnalyticsReportsEnum,
  analyticsReportsWithUrlFilters,
  isAnalyticsReportEnum,
} from './reportHelpers/reportTypes';

const reportTypePathRegex = collectEnumValues(AnalyticsReportsEnum).join('|');

// An explanation of the namespace portion of this regex: when in the 'global' namespace we don't
// have id's - so the second half of the namespace url (where we define an id like tea:123) is optional.
// Also note that we don't want to capture the overall tea:123 value when it is present, just it's component
// parts (tea and 123) - so that is a non-capturing group, denoted by the '?:' prefix
const pathnameRegExp = new RegExp(
  `/${AppRoutesPrefixesEnum.ANALYTICS}/(teammate|team|global)(?:/(tea:|tim:)?([0-9]+))?/(${reportTypePathRegex})`,
);

export function shouldSyncParamsInUrl(
  selectedViewId: AnalyticsSelectedViewId,
  reportType: AnalyticsReportsEnum,
) {
  return (
    (isSelectedViewAllData(selectedViewId) || isAnalyticsViewTemporary(selectedViewId)) &&
    analyticsReportsWithUrlFilters.includes(reportType)
  );
}

export function parseSelectedViewIdFromUrl(selectedViewId: string | undefined): AnalyticsSelectedViewId {
  const validatedSelectedViewId = RAnalyticsSelectedViewIdDirty.decode(
    Number(selectedViewId) || selectedViewId,
  );
  return E.isRight(validatedSelectedViewId) && validatedSelectedViewId.right
    ? validatedSelectedViewId.right
    : DEFAULT_VIEW_ID;
}

interface AnalyticsUrlParams {
  namespace?: string;
  selectedViewId: AnalyticsSelectedViewId;
  reportType?: AnalyticsReportsEnum;
  filters?: AnalyticsSavedViewFiltersModel;
  options?: AnalyticsReportFilterOptions;
}

export function parseInfoFromWindowLocation(): AnalyticsUrlParams {
  const {pathname, search} = window.location;

  // Parse pathname.
  const [, namespaceType, pathNamespacePrefix, namespaceId, subPage] = pathname.match(pathnameRegExp) || [];

  // Parse query params.
  const {selectedviewid, filters, selectedmetrics: options} = parseQueryString(search); // parseQueryString returns a lowercased version of the query params

  const reportType = isAnalyticsReportEnum(subPage) ? subPage : undefined;
  const selectedViewId = parseSelectedViewIdFromUrl(selectedviewid);

  const shouldReadParams = reportType && shouldSyncParamsInUrl(selectedViewId, reportType);

  const parsedFilters = shouldReadParams ? parseFiltersFromUrl(filters) : undefined;
  const parsedOptions = shouldReadParams ? parseReportFilterOptionsFromUrl(options) : undefined;

  return {
    namespace: computeNamespace(namespaceType, namespaceId, pathNamespacePrefix),
    reportType,
    selectedViewId,
    filters: parsedFilters,
    options: parsedOptions,
  };
}

export function parseReportFilterOptionsFromUrl(
  optionsString: string | undefined,
): AnalyticsReportFilterOptions | undefined {
  if (!optionsString) {
    return undefined;
  }

  try {
    const options = JSON.parse(optionsString);

    const parsedOptions = RAnalyticsReportFilterOptionsDirty.decode(options);

    if (!(E.isRight(parsedOptions) && parsedOptions.right)) {
      throw new Error('Invalid url options parameter');
    }

    return parsedOptions.right;
  } catch (err) {
    reportToBugsnag(err);
    return undefined;
  }
}

export function parseFiltersFromUrl(
  filtersString: string | undefined,
): AnalyticsSavedViewFiltersModel | undefined {
  if (!filtersString) {
    return undefined;
  }

  try {
    const filters = JSON.parse(filtersString);

    if (!RAnalyticsSavedViewFiltersDirty.is(filters)) {
      throw new Error('Invalid report filters');
    }

    return filters;
  } catch (err) {
    reportToBugsnag(err);
    return undefined;
  }
}

export const buildAnalyticsPath = (parameters: AnalyticsParameters) => {
  const {reportType, query, filters, options, namespace} = parameters;
  let specificPath;

  if (computeIsGlobalNamespace(namespace)) {
    specificPath = namespace;
  } else if (isTeamNamespace(namespace)) {
    specificPath = `team/${namespace}`;
  } else if (isTeammateNamespace(namespace)) {
    specificPath = `teammate/${namespace}`;
  }

  const path = `/${AppRoutesPrefixesEnum.ANALYTICS}/${specificPath}/${reportType}`;

  const queryParams = {
    ...query,
    filters: filters ? JSON.stringify(filters) : undefined,
    options: !isEmpty(options) ? JSON.stringify(options) : undefined,
  };

  return addQueryToPath(path, queryParams);
};

export const makeAnalyticsReportTypeUrl = (
  reportType: AnalyticsReportsEnum,
  parameters: AnalyticsParameters,
): string =>
  buildAnalyticsPath({
    ...parameters,
    reportType,
  });

export const makeAnalyticsDateTimesUrl = (
  from: number,
  to: number,
  parameters: AnalyticsParameters,
): string =>
  buildAnalyticsPath({
    ...parameters,
    query: {
      ...parameters.query,
      from,
      to,
    },
  });

export const makeAnalyticsSelectedViewUrl = (
  selectedViewId: AnalyticsSelectedViewId,
  parameters: AnalyticsParameters,
): string =>
  buildAnalyticsPath({
    ...parameters,
    query: {
      ...parameters.query,
      selectedViewId,
    },
    filters: undefined, // clear filters when changing the view
    options: undefined, // clear options when changing the view
  });

const computeNamespacePrefix = (namespaceKind: string): string => {
  if (namespaceKind === 'teammate') {
    return 'tea';
  }

  return 'tim';
};

const computeNamespace = (
  namespaceType: string,
  namespaceId: string,
  pathNamespacePrefix: string,
): string | undefined => {
  if (!namespaceType) {
    return undefined;
  }

  if (computeIsGlobalNamespace(namespaceType)) {
    return globalNamespace;
  }

  if (!namespaceId) {
    return undefined;
  }

  const namespacePrefix = pathNamespacePrefix ?? computeNamespacePrefix(namespaceType);
  return `${namespacePrefix}:${namespaceId}`;
};
